import { isEqual } from 'lodash'

import { mergePageData, checkSentimentStatus, getNodeCommentablePaths } from '@aytm/app/stats/page/comments/lib/utils'
import { DEFAULT_SEARCH_STATE } from '@aytm/app/stats/page/comments/lib/constants'

export const pageDataReducer = (state = DEFAULT_SEARCH_STATE, action) => {
  switch (action.type) {
    case 'SEARCH_INIT': {
      return {
        ...state,
        errors: [],
        isLoading: true,
        disabled: true
      }
    }
    case 'SET_PARAM': {
      const { name, value } = action.request
      return {
        ...state,
        [name]: value
      }
    }
    case 'SET_PARAMS': {
      const { value } = action.request
      return {
        ...state,
        ...value
      }
    }
    case 'SET_QA_MODE': {
      const { value } = action.request
      return {
        ...state,
        isQAMode: value,
        isQALoading: value ? true : state.isQALoading
      }
    }
    case 'ADD_PROCESSED_QA': {
      const { responseId } = action.request
      if (!state.processedQA.includes(responseId)) {
        state.processedQA.push(responseId)
      }
      return {
        ...state,
        processedQA: [...state.processedQA]
      }
    }
    case 'SET_REQUEST': {
      const { request } = action
      return isEqual(request, state.request)
        ? state
        : {
            ...state,
            request
          }
    }
    case 'SET_REQUEST_PARAM': {
      const { name, value } = action.request
      return {
        ...state,
        request: {
          ...state.request,
          [name]: value
        }
      }
    }
    case 'SET_REQUEST_PARAM_SET': {
      const { request } = action
      return isEqual(request, state.request)
        ? state
        : {
            ...state,
            request: {
              ...state.request,
              ...request
            }
          }
    }
    case 'SEARCH_RESULTS': {
      const { data, specialMode = 'comments', pageSize } = action.data
      const { survey, cs, tags, totals, unfilteredTotals } = data

      const isOnePerPage = 1 == pageSize && state.isQAMode
      const pageData = isOnePerPage
        ? mergePageData([...(state.pageData || [])], cs, state.processedQA)
        : [...(cs || [])]

      return {
        ...state,
        pageData: pageData.filter(
          (v, i, a) => v?.response && a.findIndex(_v => _v?.response && _v.response.uid == v.response.uid) == i
        ),
        tagsData: {
          tags,
          tagsStats: survey?.tagsStats || state.tagsData?.tagsStats || {},
          totals
        },
        qaTotal: state.isQAMode
          ? (!state.qaTotal && pageData.length) || (isOnePerPage && totals.responses <= state.qaTotal)
            ? totals.responses
            : state.qaTotal
          : null,
        totalPages:
          'image-responses' == specialMode
            ? Math.ceil((totals && totals.totalImageResponses ? totals.totalImageResponses : 0) / pageSize)
            : Math.ceil((totals && totals.respondents ? totals.respondents : 0) / pageSize),
        totalComments: totals.totalComments || 0,
        totalImageResponses: totals.totalImageResponses || 0,
        totalVideoResponses: totals.totalVideoResponses || 0,
        totalResponses: state.isQAMode ? totals.respondents : totals.responses,
        totalRespondents: totals.totalRespondents,
        totalVerbatims: unfilteredTotals.totalVerbatims || 0,
        totalVerbatimsByLocale: unfilteredTotals.totalVerbatimsByLocale,
        translationsMissing: unfilteredTotals.translationsMissing,
        disabled: false,
        isUpdateQATotal: false
      }
    }
    case 'TAG_MASS_UPDATED': {
      const { tags, tag, newValue } = action.data
      return {
        ...state,
        pageData: tags
          ? state.pageData.map(item => {
              item.tags = (item.tags || []).map(itemTag => {
                if (Object.hasOwn(tags, itemTag.tag) && tags[itemTag.tag].type == itemTag.type) {
                  itemTag.tag = tags[itemTag.tag].newValue
                }
                return itemTag
              })
              return item
            })
          : state.pageData.map(item => {
              item.tags = (item.tags || []).map(itemTag => {
                if (itemTag.tag == tag.tag && itemTag.type == tag.type) {
                  itemTag.tag = newValue
                }
                return itemTag
              })
              return item
            })
      }
    }
    case 'TAG_MASS_ADDED': {
      const { tags, tag, path, responseId } = action.data

      const updateTag = (item, _tag, _path) => {
        if (!item.tags.some(itemTag => itemTag.tag == _tag.tag && itemTag.type == _tag.type && itemTag.path == _path)) {
          if ('sentiment' == _tag.type) {
            if (item.tags.some(itemTag => 'sentiment' == itemTag.type && itemTag.path == _path)) {
              item.tags = item.tags.map(itemTag => {
                if ('sentiment' == itemTag.type && itemTag.path == _path) {
                  itemTag.tag = _tag.tag
                }
                return itemTag
              })
            } else if (checkSentimentStatus(_path, state.sentimentOptions)) {
              item.tags.push({ ..._tag, path: _path })
            }
          } else item.tags.push({ ..._tag, path: _path })
        }
      }

      return {
        ...state,
        pageData: tags
          ? state.pageData.map(item => {
              if (
                !responseId ||
                '*' == responseId ||
                item.response.uid == responseId ||
                (Array.isArray(responseId) && responseId.includes(item.response.uid))
              ) {
                item.tags ||= []
                Object.keys(tags).forEach(key => {
                  if (path != undefined && '*' != path) {
                    updateTag(item, tags[key], path)
                  } else {
                    getNodeCommentablePaths(item.answer, tags[key].type).forEach(path => {
                      updateTag(item, tags[key], path)
                    })
                  }
                })
              }
              return item
            })
          : state.pageData.map(item => {
              if (
                !responseId ||
                '*' == responseId ||
                item.response.uid == responseId ||
                (Array.isArray(responseId) && responseId.includes(item.response.uid))
              ) {
                item.tags ||= []
                if (path != undefined && '*' != path) {
                  updateTag(item, tag, path)
                } else {
                  getNodeCommentablePaths(item.answer, tag.type).forEach(path => {
                    updateTag(item, tag, path)
                  })
                }
              }
              return item
            })
      }
    }
    case 'TAG_MASS_CLONED': {
      const { tags, tag, newValue } = action.data
      return {
        ...state,
        pageData: tags
          ? state.pageData.map(item => {
              item.tags = (item.tags || []).reduce((acc, cur) => {
                if (Object.hasOwn(tags, cur.tag) && cur.type == tags[cur.tag].type) {
                  acc.push(Object.assign({}, cur, { tag: tags[cur.tag].newValue, type: 'manual' }))
                }
                acc.push(cur)
                return acc
              }, [])
              return item
            })
          : state.pageData.map(item => {
              item.tags = (item.tags || []).reduce((acc, cur) => {
                if (cur.tag == tag.tag && cur.type == tag.type) {
                  acc.push(Object.assign({}, cur, { tag: newValue, type: 'manual' }))
                }
                acc.push(cur)
                return acc
              }, [])
              return item
            })
      }
    }
    case 'TAG_MASS_MERGED': {
      const { tags, tag, update = false } = action.data
      return {
        ...state,
        pageData: tags
          ? state.pageData.map(item => {
              item.tags = (item.tags || []).reduce((acc, cur) => {
                const hasDestinationTag = (item.tags || []).some(
                  itemTag =>
                    itemTag.tag == tags[cur.tag].destination.tag &&
                    itemTag.type == tags[cur.tag].destination.type &&
                    itemTag.path == cur.path
                )
                if (Object.hasOwn(tags, cur.tag) && cur.type == tags[cur.tag].type && !hasDestinationTag) {
                  acc.push(
                    Object.assign({}, cur, { tag: tags[cur.tag].destination.tag, type: tags[cur.tag].destination.type })
                  )
                }
                if (!Object.hasOwn(tags, cur.tag) || !update) acc.push(cur)
                return acc
              }, [])
              return item
            })
          : state.pageData.map(item => {
              item.tags = (item.tags || []).reduce((acc, cur) => {
                const hasDestinationTag = (item.tags || []).some(
                  itemTag =>
                    itemTag.tag == tag.destination.tag &&
                    itemTag.type == tag.destination.type &&
                    itemTag.path == cur.path
                )
                if (cur.tag == tag.tag && cur.type == tag.type && !hasDestinationTag) {
                  acc.push(Object.assign({}, cur, { tag: tag.destination.tag, type: tag.destination.type }))
                }
                if (cur.tag != tag.tag || !update) acc.push(cur)
                return acc
              }, [])
              return item
            })
      }
    }
    case 'TAG_MASS_REMOVED': {
      const { tags, tag, path, responseId } = action.data

      const updateTag = (item, _tag, _path) => {
        item.tags = item.tags
          .map(itemTag => {
            if (
              itemTag.tag == _tag.tag &&
              itemTag.type == _tag.type &&
              (_path == undefined || '*' == _path || itemTag.path == _path)
            ) {
              if ('sentiment' == _tag.type) itemTag.tag = 'None'
              else return null
            }
            return itemTag
          })
          .filter(itemTag => null !== itemTag)
      }

      return {
        ...state,
        pageData: tags
          ? state.pageData.map(item => {
              if (
                !responseId ||
                '*' == responseId ||
                item.response.uid == responseId ||
                (Array.isArray(responseId) && responseId.includes(item.response.uid))
              ) {
                item.tags ||= []
                Object.keys(tags).forEach(key => {
                  updateTag(item, tags[key], path)
                })
              }
              return item
            })
          : state.pageData.map(item => {
              if (
                !responseId ||
                '*' == responseId ||
                item.response.uid == responseId ||
                (Array.isArray(responseId) && responseId.includes(item.response.uid))
              ) {
                item.tags ||= []
                updateTag(item, tag, path)
              }
              return item
            })
      }
    }
    case 'TAG_ADDED': {
      const { tag, responseId, path } = action.data
      return {
        ...state,
        pageData: state.pageData.map(item => {
          if (item.response.uid == responseId) {
            if (!item.tags) {
              item.tags = []
            }
            item.tags.push({ ...tag, type: 'manual', path })
          }
          return item
        })
      }
    }
    case 'TAG_REMOVED': {
      const { tag, responseId, path } = action.data
      return {
        ...state,
        pageData: state.pageData.map(item => {
          if (item.response.uid == responseId) {
            item.tags = item.tags.filter(
              itemTag =>
                (itemTag.type == tag.type && itemTag.tag != tag.tag && itemTag.path == path) ||
                itemTag.path !== path ||
                itemTag.type !== tag.type
            )
          }
          return item
        })
      }
    }
    case 'SENTIMENT_CHANGED': {
      const { tag, responseId, path } = action.data
      return {
        ...state,
        pageData: state.pageData.map(item => {
          if (item.response.uid == responseId) {
            if (!item.tags) {
              item.tags = []
            }
            const exisingTag = item.tags.find(itemTag => itemTag.type == tag.type && itemTag.path == path)
            if (exisingTag) Object.assign(exisingTag, tag)
            else item.tags.push({ ...tag, path })
          }
          return item
        })
      }
    }
    case 'FAILURE': {
      const { error } = action
      return {
        ...state,
        isLoading: false,
        disabled: false,
        pageData: [],
        errors: (Array.isArray(error) && error) || [error],
        totalPages: 0,
        totalResponses: 0
      }
    }
    default:
      throw new Error('Unknown action type')
  }
}

export const commentStatusesReducer = (state, action) => {
  const { responseId, scope } = action.data
  switch (action.type) {
    case 'BATCH_DATA': {
      const { batchData, isPreserveState } = action.data
      let ret
      if (isPreserveState) {
        ret = { ...state }
        Object.keys(batchData).forEach(key => {
          ret[key] = {
            ...ret[key],
            ...batchData[key]
          }
        })
      } else {
        ret = {
          ...state,
          ...batchData
        }
      }
      return ret
    }
    case 'SET_STATUS': {
      const { status } = action.data
      return {
        ...state,
        [scope]: {
          ...state[scope],
          [responseId]: status
        }
      }
    }
    case 'CLEANUP': {
      const newState = { ...state }
      delete newState[scope][responseId]
      return newState
    }
    case 'SET_TAG_STATUS': {
      const { path, status } = action.data
      return {
        ...state,
        [scope]: {
          ...state[scope],
          [responseId]: {
            ...state[scope][responseId],
            [path]: status
          }
        }
      }
    }
    case 'CLEANUP_TAG_STATUS': {
      const { path } = action.data
      const newState = { ...state }
      if (newState[scope] && newState[scope][responseId]) {
        delete newState[scope][responseId][path]
      }
      return newState
    }
    default:
      throw new Error('Unknown action type')
  }
}
